import { Helmet } from 'react-helmet-async';

export default function TermsPage() {
  return (
    <>
      <Helmet><title>Terms of Service | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Terms of Service</h1>
            <p className="mt-4 text-gray-300">Last updated: January 2026</p>
          </div>
        </div>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto prose prose-navy">
            
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing or using BESTIX by BESPOKIAN ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">2. Description of Service</h2>
              <p className="text-gray-600">
                BESTIX is a marketplace that connects ticket buyers with ticket sellers. We facilitate transactions but do not own, possess, or control the tickets listed on our platform. Sellers are independent parties responsible for fulfilling their listings.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">3. User Accounts</h2>
              <p className="text-gray-600 mb-4">To use certain features, you must create an account. You agree to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">4. Buyer Terms</h2>
              <p className="text-gray-600 mb-4">As a buyer, you understand and agree that:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>All sales are final unless covered by our Buyer Guarantee</li>
                <li>Ticket prices are set by sellers and may exceed face value</li>
                <li>You must provide accurate delivery information</li>
                <li>You are responsible for verifying event details before purchase</li>
                <li>Tickets are subject to venue terms and conditions</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">5. Seller Terms</h2>
              <p className="text-gray-600 mb-4">As a seller, you represent and warrant that:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>You have the legal right to sell the tickets listed</li>
                <li>Ticket information is accurate and complete</li>
                <li>You will deliver tickets as specified and on time</li>
                <li>You will comply with all applicable laws and venue policies</li>
                <li>You accept our commission and fee structure</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">6. Buyer Guarantee</h2>
              <p className="text-gray-600 mb-4">Our Buyer Guarantee ensures:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>You receive valid tickets in time for the event</li>
                <li>Tickets will be authentic and grant entry</li>
                <li>If we cannot resolve an issue, you receive a full refund</li>
                <li>Comparable replacement tickets if originals cannot be delivered</li>
              </ul>
              <p className="text-gray-600 mt-4">
                See our Buyer Guarantee page for complete terms and conditions.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">7. Pricing and Fees</h2>
              <p className="text-gray-600">
                All prices displayed include applicable service fees unless otherwise noted. Final pricing is shown at checkout before purchase confirmation. We reserve the right to modify our fee structure with notice to users.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">8. Prohibited Activities</h2>
              <p className="text-gray-600 mb-4">You may not:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Use the Service for fraudulent purposes</li>
                <li>List counterfeit or invalid tickets</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Attempt to bypass security measures</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Resell tickets in violation of venue or event policies</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">9. Intellectual Property</h2>
              <p className="text-gray-600">
                All content, trademarks, and intellectual property on BESTIX are owned by BESPOKIAN or its licensors. You may not use, reproduce, or distribute our content without written permission.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-600">
                To the maximum extent permitted by law, BESTIX and BESPOKIAN shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the relevant transaction.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">11. Dispute Resolution</h2>
              <p className="text-gray-600">
                Any disputes arising from these Terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except where prohibited by law.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">12. Modifications</h2>
              <p className="text-gray-600">
                We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms. We will notify users of material changes via email or prominent notice on our website.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-4">13. Contact</h2>
              <p className="text-gray-600">
                For questions about these Terms, please contact:
              </p>
              <p className="text-navy font-semibold mt-4">
                legal@bestix.ai
              </p>
            </section>

          </div>
        </div>
      </div>
    </>
  );
}
